import { IntlShape } from 'react-intl'
import { CrmIntakeView, CrmSubjectRef, CrmTrackView } from '../../../features/crm/crmApi'

// Фиксированные коды CRM, подписи - в локалях (crm<Dict>_<CODE>).
// Пакеты, типы/способы оплаты и источники - справочник из базы (см. dict.ts).
export const GRADES = ['9', '10', '11', 'COLLEGE']
export const LANGS = ['KZ', 'RU']
export const QUOTAS = ['MANY_CHILDREN', 'HALF_ORPHAN', 'ORPHAN']
export const RETURN_REASONS = ['MONEY', 'OTHER_COURSE', 'NO_TIME', 'QUALITY', 'OTHER']
export const DELETE_REASONS = ['WRONG', 'DUPLICATE', 'TEST', 'NOT_PAID']
export const FIRE_REASONS = ['OWN', 'PROBATION', 'PLAN', 'OTHER']
export const FEEDBACKS = ['OK', 'QUESTION', 'PROBLEM', 'NO_REACH'] as const
export const SALES_GROUPS = ['OP_KZ', 'OP_RU']

export type Dict = 'Grade' | 'Lang' | 'Quota' | 'Ret' | 'Fb' | 'Group' | 'Del' | 'Fire'

export const dictLabel = (fm: IntlShape['formatMessage'], dict: Dict, code?: string | null) =>
    code ? fm({ id: `crm${dict}_${code}`, defaultMessage: code }) : '-'

export const dictList = (fm: IntlShape['formatMessage'], dict: Dict, codes?: string[] | null) =>
    (codes ?? []).map((c) => dictLabel(fm, dict, c)).join(', ')

export const STEP_NOS = [1, 2, 3, 4, 5]

// Короткие имена предметов для полоски модератора: по названию, не по id
const ABBR: [RegExp, string, string][] = [
    [/(қазақстан|казахстан).*тарих|истори.*казахстан/i, 'ҚТарих', 'ИстКЗ'],
    [/дүниежүзі|всемирн/i, 'ДЖТ', 'ВИ'],
    [/географ/i, 'Гео', 'Гео'],
    [/информат/i, 'Инф', 'Инф'],
    [/математ/i, 'Мат', 'Мат'],
    [/ағылшын|шетел|англ|иностран/i, 'Ағыл', 'Англ'],
    [/құқық|прав/i, 'Құқық', 'Право'],
    [/биолог/i, 'Био', 'Био'],
    [/хими/i, 'Хим', 'Хим'],
    [/физик/i, 'Физ', 'Физ'],
]

export const subjectAbbr = (s: CrmSubjectRef | null | undefined, locale: string) => {
    const t = `${s?.titleKz || ''} ${s?.titleRu || ''}`
    const hit = ABBR.find(([re]) => re.test(t))
    if (hit) return locale === 'ru' ? hit[2] : hit[1]
    return subjectTitle(s, locale).slice(0, 5)
}

export const subjectTitle = (s: CrmSubjectRef | null | undefined, locale: string) =>
    s ? (locale === 'ru' ? s.titleRu || s.titleKz : s.titleKz || s.titleRu) : ''

export const subjectsLine = (list: CrmSubjectRef[] | null | undefined, locale: string) =>
    (list ?? []).map((s) => subjectTitle(s, locale)).join(', ')

export const courseTitle = (x: Pick<CrmIntakeView | CrmTrackView, 'courseTitleKz' | 'courseTitleRu'>, locale: string) =>
    (locale === 'ru' ? x.courseTitleRu || x.courseTitleKz : x.courseTitleKz || x.courseTitleRu) || ''

export const digits = (v: string | null | undefined) => (v || '').replace(/\D/g, '')

// «8 701…», «701…», «+7 701…» -> «+7 701 555 12 34»
export const formatPhone = (v: string | null | undefined) => {
    let d = digits(v)
    // iam хранит 10 цифр без 7
    if (d.length === 10) d = '7' + d
    else if (d.startsWith('8')) d = '7' + d.slice(1)
    if (d && !d.startsWith('7')) d = '7' + d
    d = d.slice(0, 11)
    return d ? '+7 ' + [d.slice(1, 4), d.slice(4, 7), d.slice(7, 9), d.slice(9, 11)].filter(Boolean).join(' ') : ''
}

export const pad = (n: number) => String(n).padStart(2, '0')

export const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

export const todayKey = () => dayKey(new Date())

export const addMonthKey = (key: string) => {
    const d = new Date(`${key}T00:00:00`)
    d.setMonth(d.getMonth() + 1)
    return dayKey(d)
}

// сколько дней от сегодня до yyyy-MM-dd (минус - прошло)
export const daysTo = (key?: string | null) =>
    key ? Math.round((new Date(`${key.slice(0, 10)}T00:00:00`).getTime() - new Date(`${todayKey()}T00:00:00`).getTime()) / 86400000) : 0

export const parse = (iso?: string | null) => {
    if (!iso) return null
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? null : d
}

export const dayKeyOf = (iso?: string | null) => {
    const d = parse(iso)
    return d ? dayKey(d) : ''
}

export const hm = (iso?: string | null) => {
    const d = parse(iso)
    return d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : ''
}

// yyyy-MM-dd или ISO -> dd.MM.yyyy
export const fmtDate = (iso?: string | null) => {
    if (!iso) return '-'
    const key = iso.length === 10 ? iso : dayKeyOf(iso)
    return key ? key.split('-').reverse().join('.') : '-'
}

// «16:20» сегодня, «15.10 16:20» в другие дни
export const whenShort = (iso?: string | null) => {
    if (!iso) return ''
    return dayKeyOf(iso) === todayKey() ? hm(iso) : `${fmtDate(iso).slice(0, 5)} ${hm(iso)}`
}

export const money = (n?: number | null) => `${Math.round(n ?? 0).toLocaleString('ru-RU').replace(/\s/g, ' ')} ₸`

export const amoId = (url?: string | null) => ((url || '').match(/(\d+)\/?$/) || [])[1] || '-'

export const initials = (name?: string | null) =>
    (name || '')
        .split(' ')
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()

export type Preset = 'today' | 'yesterday' | 'week' | 'month' | 'all' | ''

export const presetRange = (k: Preset): [string, string] => {
    const t = new Date()
    t.setHours(0, 0, 0, 0)
    const f = new Date(t)
    let to = new Date(t)
    if (k === 'yesterday') {
        f.setDate(f.getDate() - 1)
        to = new Date(f)
    } else if (k === 'week') {
        f.setDate(f.getDate() - ((t.getDay() + 6) % 7))
    } else if (k === 'month') {
        f.setDate(1)
    } else if (k === 'all') {
        f.setFullYear(2020, 0, 1)
    }
    return [dayKey(f), dayKey(to)]
}

// iam id текущего пользователя из JWT (клейм iam_user_id)
export const iamUserId = (token?: string | null) => {
    try {
        const part = (token || '').split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
        return Number(JSON.parse(atob(part)).iam_user_id) || 0
    } catch {
        return 0
    }
}

const PASS_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'

export const genPassword = () => {
    const bytes = new Uint32Array(8)
    window.crypto.getRandomValues(bytes)
    return Array.from(bytes, (b) => PASS_ALPHABET[b % PASS_ALPHABET.length]).join('')
}

// Фото с телефона - 4-12 МБ. Перед отправкой ужимаем до ~1600px JPEG; не картинка или не декодируется - как есть.
export const downscaleImage = (file: File, max = 1600, quality = 0.85): Promise<File> =>
    new Promise((resolve) => {
        if (!file.type.startsWith('image/') || file.type === 'image/gif') return resolve(file)
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => {
            URL.revokeObjectURL(url)
            const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight))
            if (scale === 1 && file.size < 1024 * 1024) return resolve(file)
            const canvas = document.createElement('canvas')
            canvas.width = Math.round(img.naturalWidth * scale)
            canvas.height = Math.round(img.naturalHeight * scale)
            const ctx = canvas.getContext('2d')
            if (!ctx) return resolve(file)
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
            canvas.toBlob(
                (blob) => {
                    if (!blob) return resolve(file)
                    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg'
                    resolve(new File([blob], name, { type: 'image/jpeg' }))
                },
                'image/jpeg',
                quality,
            )
        }
        img.onerror = () => {
            URL.revokeObjectURL(url)
            resolve(file)
        }
        img.src = url
    })

export const isImageUrl = (url?: string | null) => /\.(png|jpe?g|webp|gif|bmp|heic)(\?|$)/i.test(url || '')
